import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BedDouble, Users, ChefHat, Wind, Wifi, Bath, Coffee, Sofa, Armchair } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const V3_IMGS = [
  "v3-sala-1.jpg","v3-sala-2.jpg","v3-sala-3.jpg","v3-sala-4.jpg","v3-sala-5.jpg","v3-sala-6.jpg","v3-sala-7.jpg",
  "v3-cozinha-1.jpg","v3-cozinha-2.jpg","v3-cozinha-3.jpg",
  "v3-quarto1-1.jpg","v3-quarto2-1.jpg","v3-quarto2-2.jpg","v3-quarto2-3.jpg","v3-quarto2-4.jpg",
  "v3-quarto3-1.jpg","v3-wc-1.jpg","v3-wc-2.jpg","v3-wc-3.jpg",
].map(f => `/images/${f}`);

const V1_IMGS = [
  "v1-sala-1.jpg","v1-sala-2.jpg","v1-cozinha-1.jpg","v1-cozinha-2.jpg",
  "v1-quarto-1.jpg","v1-quarto-2.jpg","v1-quarto-3.jpg","v1-wc-1.jpg","v1-wc-2.jpg",
].map(f => `/images/${f}`);

const SHARED_IMGS = [
  "villa-terraco.jpg",
  "villa-exterior-3.jpg",
  "villa-exterior-4.jpg",
  "villa-exterior-1.jpg",
  "villa-exterior-2.jpg",
  "villa-exterior-5.jpg",
].map(f => `/images/${f}`);

const ICON_MAP = [BedDouble, Bath, ChefHat, Wind, Armchair, Wifi, Sofa, Users];
const ICON_MAP_V1 = [BedDouble, Bath, Coffee, Wind, Wifi, Users];

export default function Properties() {
  const { t } = useLang();

  return (
    <section id="properties" data-testid="properties-section" className="relative py-24 md:py-40 bg-[#F0EBE1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-20 md:mb-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-6" data-testid="properties-eyebrow">{t.properties.eyebrow}</p>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#112A46] leading-[1.02] tracking-tight font-light" data-testid="properties-title">
              {t.properties.title_1}<span className="italic">{t.properties.title_italic}</span>
            </h2>
            <p className="mt-6 text-[#5C665F] text-base md:text-lg max-w-xl">{t.properties.subtitle}</p>
          </div>
          <div className="md:col-span-5 aspect-[4/3] overflow-hidden hidden md:block">
            <img src="/images/villa-terraco.jpg" alt="Villa d'Avó Dalva" className="w-full h-full object-cover kb-image" loading="lazy" />
          </div>
        </div>

        <PropertyBlock
          id="v3"
          reversed={false}
          images={V3_IMGS}
          name={t.properties.v3_name}
          tag={t.properties.v3_tag}
          desc={t.properties.v3_desc}
          features={t.properties.v3_features}
          icons={ICON_MAP}
          price="200"
          per_night={t.properties.per_night}
          reserve={t.properties.reserve}
          option="v3"
        />

        <div className="h-24 md:h-32" />

        <PropertyBlock
          id="v1"
          reversed={true}
          images={V1_IMGS}
          name={t.properties.v1_name}
          tag={t.properties.v1_tag}
          desc={t.properties.v1_desc}
          features={t.properties.v1_features}
          icons={ICON_MAP_V1}
          price="100"
          per_night={t.properties.per_night}
          reserve={t.properties.reserve}
          option="v1"
        />

        <SharedSpace t={t} />
      </div>
    </section>
  );
}

function SharedSpace({ t }) {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % SHARED_IMGS.length);
  const prev = () => setIdx((i) => (i - 1 + SHARED_IMGS.length) % SHARED_IMGS.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      data-testid="shared-space"
      className="mt-32 md:mt-40 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
    >
      <div className="md:col-span-7 relative group">
        <div className="relative aspect-[3/2] overflow-hidden bg-[#112A46]">
          <AnimatePresence mode="wait">
            <motion.img
              key={SHARED_IMGS[idx]}
              src={SHARED_IMGS[idx]}
              alt={`Espaço exterior ${idx + 1}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="shared-image"
            />
          </AnimatePresence>

          <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button onClick={prev} data-testid="shared-prev"
              className="bg-[#FAFAF7]/90 hover:bg-[#FAFAF7] p-3 text-[#112A46]" aria-label="Previous">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button onClick={next} data-testid="shared-next"
              className="bg-[#FAFAF7]/90 hover:bg-[#FAFAF7] p-3 text-[#112A46]" aria-label="Next">
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>

          <div className="absolute bottom-4 right-4 bg-[#112A46]/80 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] px-3 py-2">
            {String(idx + 1).padStart(2, "0")} / {String(SHARED_IMGS.length).padStart(2, "0")}
          </div>
        </div>
      </div>

      <div className="md:col-span-5 space-y-6">
        <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-8" data-testid="shared-eyebrow">{t.properties.shared_eyebrow}</p>
        <p className="text-[#5C665F] leading-relaxed text-base md:text-lg" data-testid="shared-p1">{t.properties.shared_p1}</p>
        <p className="text-[#5C665F] leading-relaxed text-base md:text-lg" data-testid="shared-p2">{t.properties.shared_p2}</p>
      </div>
    </motion.div>
  );
}

function PropertyBlock({ id, reversed, images, name, tag, desc, features, icons, price, per_night, reserve, option }) {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  const handleReserve = () => {
    try { localStorage.setItem("vad_pref_acc", option); } catch {}
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`property-${id}`}
      className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center`}
    >
      <div className={`md:col-span-7 relative group ${reversed ? "md:order-2" : ""}`}>
        <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-[#112A46]">
          <AnimatePresence mode="wait">
            <motion.img
              key={images[idx]}
              src={images[idx]}
              alt={`${name} ${idx + 1}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`property-${id}-image`}
            />
          </AnimatePresence>

          <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button onClick={prev} data-testid={`property-${id}-prev`}
              className="bg-[#FAFAF7]/90 hover:bg-[#FAFAF7] p-3 text-[#112A46]" aria-label="Previous">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button onClick={next} data-testid={`property-${id}-next`}
              className="bg-[#FAFAF7]/90 hover:bg-[#FAFAF7] p-3 text-[#112A46]" aria-label="Next">
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>

          <div className="absolute bottom-4 right-4 bg-[#112A46]/80 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] px-3 py-2">
            {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>
        </div>
      </div>

      <div className={`md:col-span-5 ${reversed ? "md:order-1" : ""}`}>
        <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-4">{tag}</p>
        <h3 className="font-serif text-5xl md:text-6xl text-[#112A46] font-light tracking-tight leading-none" data-testid={`property-${id}-name`}>{name}</h3>
        <p className="mt-6 text-[#5C665F] leading-relaxed text-base">{desc}</p>

        <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
          {features.map((f, i) => {
            const Icon = icons[i] || BedDouble;
            return (
              <li key={i} className="flex items-start gap-3 text-sm text-[#1C2321]" data-testid={`property-${id}-feature-${i}`}>
                <Icon size={16} strokeWidth={1.25} className="text-[#112A46] mt-0.5 flex-shrink-0" />
                <span className="font-light leading-snug">{f}</span>
              </li>
            );
          })}
        </ul>

        <div className="mt-10 pt-8 border-t border-[#112A46]/10 flex justify-end">
          <a
            href="#contact"
            onClick={handleReserve}
            data-testid={`property-${id}-reserve-btn`}
            className="inline-block bg-[#112A46] text-[#FAFAF7] px-8 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#C48E66] transition-all duration-300"
          >
            {reserve}
          </a>
        </div>
      </div>
    </motion.div>
  );
}
